import { legalLang } from "@kawkaw/shared";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getClient } from "./client";
import { useAuthStore } from "../store/auth";
import type {
  Order, Transaction, MenuItem, Stall, Staff,
  Voucher, Bundle, ActivityLog, StallApplication, PaymentMethodConfig, DeliveryProvider, PromoBanner,
  LegalDocument, LegalAppScope, LegalDocType, LegalLanguage, SmsProvider,
} from "@kawkaw/shared";

const api = () => getClient();

// Generic helper: backend returns { code, msg, data: T } via reply.ok()
type ApiOk<T> = { code: number; msg: string; data: T };
// Paginated backend shape: reply.ok({ items, page, total })
type PagedOk<T> = ApiOk<{ items: T[]; page: number; total: number }>;

/* ── Auth ── */
export function useAdminLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api().post("auth/admin/login", { json: body }).json<{
        data: { admin: { id: string; name: string; role: string; permissions: string[]; kopitiam_id: string }; tokens: { accessToken: string; refreshToken: string } };
      }>(),
    onSuccess: () => qc.clear(),
  });
}

/* ── Dashboard stats ── */
export function useDashboardStats() {
  return useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => api().get("admin/reports/dashboard").json<ApiOk<{
      todayRevenue: string;
      todayOrders: number;
      pickupOrders: number;
      deliveryOrders: number;
      pendingRefunds: number;
      activeStaff: number;
      newNow: number;
      preparingNow: number;
      readyForPickup: number;
      readyForDelivery: number;
      activeNow: number;
      revChangePercent: number | null;
    }>>(),
    refetchInterval: 30_000,
  });
}

export interface AdminDailyReport {
  date: string;
  totalOrders: number;
  totalRevenue: string;
  avgTicketSize: string;
  ordersByStatus: Record<string, number>;
  hourly: Array<{ hour: number; orders: number; revenue: string }>;
  topItems: Array<{ menuItemId: string; name: string; qty: number; revenue: string; imageUrl?: string | null }>;
  kitchenLoad: { active: number; capacity: number };
}

export interface AdminTopItem {
  menuItemId: string;
  name: string;
  qty: number;
  revenue: string;
  imageUrl?: string | null;
}

export function useAdminDailyReport(date?: string) {
  return useQuery({
    queryKey: ["admin", "reports", "daily", date ?? "today"],
    queryFn: () => api().get(`admin/reports/daily${date ? `?date=${date}` : ""}`).json<ApiOk<AdminDailyReport>>(),
    refetchInterval: 60_000,
  });
}

export function useAdminTopItems(params: { from?: string; to?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: ["admin", "reports", "top-items", params],
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null) sp.set(k, String(v));
      }
      const query = sp.toString();
      return api().get(`admin/reports/top-items${query ? `?${query}` : ""}`).json<ApiOk<AdminTopItem[]>>();
    },
    refetchInterval: 60_000,
  });
}

/* ── Orders ── */
export function useAdminOrders(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ["admin", "orders", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null) sp.set(k, String(v)); }
      const res = await api().get(`admin/orders?${sp}`).json<PagedOk<Order>>();
      return {
        data: res.data.items ?? [],
        meta: { total: res.data.total, page: res.data.page, limit: Number(params.limit ?? 20) },
      };
    },
    refetchInterval: 15_000, // rider/Lalamove status changes arrive server-side
  });
}

export function useAdminOrder(id: string) {
  return useQuery({
    queryKey: ["admin", "orders", id],
    queryFn: () => api().get(`admin/orders/${id}`).json<ApiOk<Order>>(),
    enabled: !!id,
    refetchInterval: 15_000,
  });
}

/**
 * Fetches every order matching the given filters (not just the current
 * page) for CSV export — the Orders screen paginates, but an export needs
 * every matching row.
 */
export async function fetchAdminOrdersForExport(params: Record<string, string | number | undefined>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) { if (v != null && k !== "page" && k !== "limit") sp.set(k, String(v)); }
  sp.set("limit", "200"); // GET /admin/orders caps limit at 200
  const res = await api().get(`admin/orders?${sp}`).json<PagedOk<Order>>();
  return res.data.items ?? [];
}

export function useAdminCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api().post(`admin/orders/${id}/cancel`, { json: { reason } }).json<ApiOk<Order>>(),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin", "orders"] });
      void qc.invalidateQueries({ queryKey: ["admin", "orders", vars.id] });
      void qc.invalidateQueries({ queryKey: ["admin", "transactions"] });
      void qc.invalidateQueries({ queryKey: ["admin", "reports"] });
    },
  });
}

export function useAdminUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: string; note?: string }) =>
      api().patch(`admin/orders/${id}/status`, { json: { status, ...(note ? { note } : {}) } }).json<ApiOk<Order>>(),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin", "orders"] });
      void qc.invalidateQueries({ queryKey: ["admin", "orders", vars.id] });
    },
  });
}

/* ── Transactions ── */
export function useAdminTransactions(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ["admin", "transactions", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null) sp.set(k, String(v)); }
      const res = await api().get(`admin/transactions?${sp}`).json<PagedOk<Transaction>>();
      return {
        data: res.data.items ?? [],
        meta: { total: res.data.total, page: res.data.page, limit: Number(params.limit ?? 20) },
      };
    },
  });
}

/**
 * Fetches every transaction matching the given filters (not just the current
 * page) for CSV export — the Transactions screen paginates at 10/page, but an
 * export needs every matching row.
 */
export async function fetchAdminTransactionsForExport(params: Record<string, string | number | undefined>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) { if (v != null && k !== "page" && k !== "limit") sp.set(k, String(v)); }
  sp.set("limit", "1000");
  const res = await api().get(`admin/transactions?${sp}`).json<PagedOk<Transaction>>();
  return res.data.items ?? [];
}

export function useAdminTransactionsSummary(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ["admin", "transactions", "summary", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null) sp.set(k, String(v)); }
      return api().get(`admin/transactions/summary?${sp}`).json<ApiOk<{ totalRevenue: string; totalRefunded: string; netRevenue: string }>>();
    },
  });
}

export type TxnDetail = Transaction & {
  order: {
    id: string; order_code: string; type: string; created_at: string;
    special_note?: string | null;
    address?: string | null;
    cancel_reason?: string | null;
    customer_name?: string | null;
    customer_phone?: string | null;
    subtotal: string; sst_amount: string; delivery_fee: string; total: string;
    voucher_code: string | null; voucher_discount: string;
    customer: { id: string; name: string | null; phone: string; email: string | null } | null;
    kopitiam: { name: string; address: string; phone: string };
    items: Array<{ id: string; name: string; qty: number; price: string; options: unknown; note?: string | null }>;
  };
  refunds: Array<{
    id: string; amount: string; type: string; reason: string;
    method: string; status: string; created_at: string; rejection_note: string | null;
  }>;
  activity_logs: Array<{
    id: string; action: string; entity_type: string; entity_id: string;
    meta: Record<string, unknown> | null; created_at: string;
    staff: { name: string } | null;
  }>;
};

export function useAdminTransaction(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "transactions", id],
    queryFn: () => api().get(`admin/transactions/${id}`).json<ApiOk<TxnDetail>>().then(r => r.data),
    enabled: !!id,
  });
}

/* ── Refunds ── */
export interface AdminRefundItem {
  id: string; amount: string; type: string; reason: string; method: string;
  status: string; rejection_note: string | null;
  created_at: string; processed_at: string | null;
  transaction_id: string;
  transaction: { id: string; method: string; amount: string; reference: string | null };
  order: { id: string; order_code: string; customer: { id: string; name: string | null } | null };
  // true iff this refund's transaction's provider can auto-refund via gateway
  // (Fiuu today) AND the transaction has a persisted gateway reference — see
  // GET /admin/refunds in packages/backend/src/modules/admin/routes.ts.
  autoRefundSupported: boolean;
}

export interface AdminRefundDetail extends AdminRefundItem {
  customer: { id: string; name: string | null; phone: string; email: string | null } | null;
  activity_logs: Array<{
    id: string; action: string; created_at: string;
    meta: Record<string, unknown> | null;
    staff: { name: string } | null;
  }>;
}

export function useInitiateRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { orderId: string; amount: string; type: "full" | "partial"; reason: string; method: string }) =>
      api().post("admin/refunds", { json: { ...body, amount: Number(body.amount) } }).json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "refunds"] });
      void qc.invalidateQueries({ queryKey: ["admin", "transactions"] });
      void qc.invalidateQueries({ queryKey: ["admin", "orders"] });
    },
  });
}

export function useRefundStats() {
  return useQuery({
    queryKey: ["admin", "refunds", "stats"],
    queryFn: () => api().get("admin/refunds/stats").json<ApiOk<{ totalAmount: string; lastMonthAmount: string; changePercent: number | null; totalCount: number; partialCount: number; todayAmount: string }>>().then(r => r.data),
    refetchInterval: 60_000,
  });
}

export function useAdminRefunds(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ["admin", "refunds", "list", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null) sp.set(k, String(v)); }
      const res = await api().get(`admin/refunds?${sp}`).json<PagedOk<AdminRefundItem>>();
      return {
        data: res.data.items ?? [],
        meta: { total: res.data.total, page: res.data.page, limit: Number(params.limit ?? 20) },
      };
    },
  });
}

export function useAdminRefund(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "refunds", id],
    queryFn: () => api().get(`admin/refunds/${id!}`).json<ApiOk<AdminRefundDetail>>().then(r => r.data),
    enabled: !!id,
  });
}

export function useReviewRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, note, transfer_method, transfer_time }: {
      id: string; action: "approve" | "reject"; note?: string;
      transfer_method?: string; transfer_time?: string;
    }) =>
      api().patch(`admin/refunds/${id}`, { json: { decision: action, note, transfer_method, transfer_time } })
        .json<ApiOk<{ status: string; rejection_note: string | null }>>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "refunds"] });
    },
  });
}

/* ── Menu ── */
export function useAdminStalls() {
  const hasToken = !!useAuthStore(s => s.accessToken);
  return useQuery({
    queryKey: ["admin", "stalls"],
    queryFn: () => api().get("admin/stalls").json<ApiOk<Stall[]>>(),
    enabled: hasToken,
    // refetchOnWindowFocus is off app-wide; without this, one admin's stall
    // edit is invisible to another admin's already-open Stalls list.
    refetchInterval: 30_000,
  });
}

export function useCreateStall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string | null; is_open?: boolean; prep_time?: number; auto_accept?: boolean; sort_order?: number; cover_image?: string | null; sst_rate?: number | null }) =>
      api().post("admin/stalls", { json: body }).json<ApiOk<Stall>>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "stalls"] });
      void qc.invalidateQueries({ queryKey: ["admin", "menu"] });
    },
  });
}

export function useUpdateStall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string; name?: string; description?: string | null; is_open?: boolean; prep_time?: number; auto_accept?: boolean; sort_order?: number; cover_image?: string | null;
      commission_threshold?: number | null; commission_flat_fee?: number | null; commission_percent?: number | null;
      addon_commission_percent?: number | null; sst_rate?: number | null;
    }) =>
      api().patch(`admin/stalls/${id}`, { json: body }).json<ApiOk<Stall>>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "stalls"] });
      void qc.invalidateQueries({ queryKey: ["admin", "menu"] });
      void qc.invalidateQueries({ queryKey: ["admin", "reports", "top-items-by-stall"] });
    },
  });
}

export function useDeleteStall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/stalls/${id}`).json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "stalls"] });
      void qc.invalidateQueries({ queryKey: ["admin", "menu"] });
    },
  });
}

export function useAdminMenuItems(stallId?: string) {
  return useQuery({
    queryKey: ["admin", "menu", stallId],
    queryFn: () => api().get(`admin/menu/items${stallId ? `?stall_id=${stallId}` : ""}`).json<ApiOk<MenuItem[]>>(),
    enabled: !!stallId,
    // Same reasoning as useAdminStalls: refetchOnWindowFocus is off app-wide.
    refetchInterval: 30_000,
  });
}

export function useAdminAllMenuItems() {
  return useQuery({
    queryKey: ["admin", "menu", "all"],
    queryFn: () => api().get("admin/menu/items").json<ApiOk<MenuItem[]>>(),
  });
}

export function useCreateMenuItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => {
      const payload = { ...body };
      // Ensure numeric fields are numbers
      if (payload.price != null) payload.price = Number(payload.price);
      if (payload.offer_price != null) payload.offer_price = Number(payload.offer_price);
      // Remove display-only fields
      delete payload.option_groups; // managed separately
      delete payload.categories; // managed via category_ids
      return api().post("admin/menu/items", { json: payload }).json();
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

/** Persist a drag-and-drop re-order: `ids` is the stall's full item list in display order. Optimistic. */
export function useReorderMenuItems(stallId?: string) {
  const qc = useQueryClient();
  const key = ["admin", "menu", stallId];
  return useMutation({
    mutationFn: (ids: string[]) => api().post("admin/menu/items/reorder", { json: { stall_id: stallId, ids } }).json(),
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ApiOk<MenuItem[]>>(key);
      if (prev) {
        const pos = new Map(ids.map((id, i) => [id, i]));
        qc.setQueryData(key, { ...prev, data: [...prev.data].sort((x, y) => (pos.get(x.id) ?? 1e9) - (pos.get(y.id) ?? 1e9)) });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

export function useUpdateMenuItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) => {
      const payload = { ...body };
      if (payload.price != null) payload.price = Number(payload.price);
      if (payload.offer_price != null) payload.offer_price = Number(payload.offer_price);
      delete payload.option_groups;
      delete payload.categories; // managed via category_ids
      return api().patch(`admin/menu/items/${id}`, { json: payload }).json();
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

export function useDeleteMenuItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/menu/items/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

/* ── Option Groups ── */
export function useCreateOptionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, ...body }: { itemId: string; name: string; type: "required" | "optional"; min_select: number; max_select: number; sort_order?: number; icon_key?: string; category_group_id?: string }) =>
      api().post(`admin/menu/items/${itemId}/option-groups`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

export function useUpdateOptionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; type?: "required" | "optional"; min_select?: number; max_select?: number; sort_order?: number; icon_key?: string | null }) =>
      api().patch(`admin/menu/option-groups/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

export function useDeleteOptionGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/menu/option-groups/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

/* ── Option Choices ── */
export function useCreateOptionChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, ...body }: { groupId: string; label: string; extra_charge?: number; sort_order?: number; input_type?: ChoiceInputType }) =>
      api().post(`admin/menu/option-groups/${groupId}/choices`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

export function useUpdateOptionChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; label?: string; extra_charge?: number; sort_order?: number; input_type?: ChoiceInputType }) =>
      api().patch(`admin/menu/option-choices/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

export function useDeleteOptionChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/menu/option-choices/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu"] }); },
  });
}

/* ── Menu Categories ── */
export type ChoiceInputType = "radio" | "quantity" | "input" | "textarea";
export interface CategoryChoice { label: string; extra_charge?: number; input_type?: ChoiceInputType }
export interface CategoryOptionGroup { id: string; name: string; choices: Array<string | CategoryChoice>; max_select?: number }
export interface MenuCategoryRow { id: string; stall_id: string | null; name: string; emoji: string | null; icon_url: string | null; sort_order: number; default_option_groups: CategoryOptionGroup[] | null }

export function useAdminMenuCategories(stallId?: string) {
  const hasToken = !!useAuthStore(s => s.accessToken);
  return useQuery({
    queryKey: ["admin", "menu-categories", stallId ?? "all"],
    queryFn: () => {
      const url = stallId
        ? `admin/menu/categories?stall_id=${stallId}`
        : "admin/menu/categories";
      return api().get(url).json<ApiOk<MenuCategoryRow[]>>();
    },
    enabled: hasToken,
  });
}

export function useCreateMenuCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { stall_id?: string; name: string; emoji?: string; icon_url?: string | null; sort_order?: number; default_option_groups?: CategoryOptionGroup[] | null }) =>
      api().post("admin/menu/categories", { json: body }).json<ApiOk<MenuCategoryRow>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu-categories"] }); },
  });
}

export function useUpdateMenuCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; emoji?: string | null; icon_url?: string | null; sort_order?: number; stall_id?: string | null; default_option_groups?: CategoryOptionGroup[] | null }) =>
      api().patch(`admin/menu/categories/${id}`, { json: body }).json<ApiOk<MenuCategoryRow>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "menu-categories"] }); },
  });
}

export function useDeleteMenuCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api().delete(`admin/menu/categories/${id}`).json(),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin", "menu-categories"] });
      void qc.invalidateQueries({ queryKey: ["admin", "menu"] });
    },
  });
}

export function useCloudinarySign() {
  return useMutation({
    mutationFn: (folder: string) =>
      api().post("admin/uploads/image", { json: { folder } }).json<ApiOk<{ signature: string; timestamp: number; apiKey: string; cloudName: string; folder: string }>>(),
  });
}

/* ── Vouchers ── */
export function useAdminVouchers() {
  return useQuery({
    queryKey: ["admin", "vouchers"],
    queryFn: async () => {
      type RawVoucher = { id: string; code: string; name: string; type: string; value: string; min_order: string; total_uses: number | null; used_count: number; status: string; valid_from: string; valid_to: string };
      const res = await api().get("admin/vouchers").json<ApiOk<RawVoucher[]>>();
      return {
        ...res,
        data: res.data.map((v) => ({
          ...v,
          discount_type: v.type,
          discount_value: v.value,
          min_spend: v.min_order,
          usage_limit: v.total_uses,
          valid_until: v.valid_to,
        })),
      };
    },
  });
}

export function useCreateVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => {
      // Map screen field names → backend field names
      const payload: Record<string, unknown> = { ...body };
      if ("discount_type" in payload) { payload.type = payload.discount_type; delete payload.discount_type; }
      if ("discount_value" in payload) { payload.value = Number(payload.discount_value); delete payload.discount_value; }
      if ("min_spend" in payload) { payload.min_order = Number(payload.min_spend ?? 0); delete payload.min_spend; }
      if ("usage_limit" in payload) { payload.total_uses = payload.usage_limit; delete payload.usage_limit; }
      if ("valid_until" in payload) { payload.valid_to = payload.valid_until; delete payload.valid_until; }
      // Remove screen-only fields
      delete payload.is_single_use;
      return api().post("admin/vouchers", { json: payload }).json();
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "vouchers"] }); },
  });
}

export function useUpdateVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) => {
      // Map screen field names → backend field names
      const payload: Record<string, unknown> = { ...body };
      if ("discount_type" in payload) { payload.type = payload.discount_type; delete payload.discount_type; }
      if ("discount_value" in payload) { payload.value = Number(payload.discount_value); delete payload.discount_value; }
      if ("min_spend" in payload) { payload.min_order = Number(payload.min_spend ?? 0); delete payload.min_spend; }
      if ("usage_limit" in payload) { payload.total_uses = payload.usage_limit; delete payload.usage_limit; }
      if ("valid_until" in payload) { payload.valid_to = payload.valid_until; delete payload.valid_until; }
      delete payload.is_single_use;
      return api().patch(`admin/vouchers/${id}`, { json: payload }).json();
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "vouchers"] }); },
  });
}

export function useDeleteVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/vouchers/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "vouchers"] }); },
  });
}

export interface VoucherUsageRow {
  orderId: string; orderCode: string; status: string; createdAt: string;
  orderTotal: string; discountAmount: string; slot: "slot1" | "slot2" | "delivery";
  customer: { id: string; name: string | null; phone: string } | null;
}

export function useVoucherUsage(voucherId: string | null) {
  return useQuery({
    queryKey: ["admin", "vouchers", voucherId, "usage"],
    queryFn: () => api().get(`admin/vouchers/${voucherId}/usage`).json<ApiOk<{ voucherId: string; code: string; usedCount: number; orders: VoucherUsageRow[] }>>(),
    enabled: !!voucherId,
  });
}

export function useDistributeVoucher() {
  return useMutation({
    mutationFn: ({ id, customerIds }: { id: string; customerIds: string[] }) =>
      api().post(`admin/vouchers/${id}/distribute`, { json: { customerIds } }).json<ApiOk<{ distributed: number }>>(),
  });
}

export function useAdminCustomers(search: string) {
  return useQuery({
    queryKey: ["admin", "customers", search],
    queryFn: () =>
      api().get("admin/customers", { searchParams: search ? { search } : {} }).json<ApiOk<Array<{ id: string; phone: string; name: string | null }>>>(),
    enabled: search.length > 0,
  });
}

/* ── Bundles ── */
export function useAdminBundles() {
  return useQuery({
    queryKey: ["admin", "bundles"],
    queryFn: () => api().get("admin/bundles").json<ApiOk<Bundle[]>>(),
  });
}

export function useCreateBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Bundle>) => api().post("admin/bundles", { json: body }).json<ApiOk<Bundle>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "bundles"] }); },
  });
}

export function useUpdateBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Bundle> & { id: string }) =>
      api().patch(`admin/bundles/${id}`, { json: body }).json<ApiOk<Bundle>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "bundles"] }); },
  });
}

export function useDeleteBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/bundles/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "bundles"] }); },
  });
}

/* ── Staff ── */
export function useAdminStaff() {
  return useQuery({
    queryKey: ["admin", "staff"],
    queryFn: () => api().get("admin/staff").json<ApiOk<Staff[]>>(),
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    // Backend's Zod schema expects `stall_id` (snake_case) — silently dropped
    // (never persisted) if sent as `stallId`, since z.object().parse() strips
    // unrecognized keys with no error. Translate at the boundary so callers can
    // keep using the app's normal camelCase convention.
    mutationFn: ({ stallId, ...body }: {
      name: string; phone: string; role: string; stallId?: string; pin?: string;
      email?: string; password?: string; permissions?: string[];
    }) =>
      api().post("admin/staff", { json: { ...body, stall_id: stallId || undefined } }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "staff"] }); },
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stallId, ...body }: {
      id: string; name?: string; role?: string; stallId?: string; is_active?: boolean;
      permissions?: string[]; password?: string;
    }) =>
      api().patch(`admin/staff/${id}`, { json: { ...body, stall_id: stallId !== undefined ? (stallId || null) : undefined } }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "staff"] }); },
  });
}

export function useDeleteStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/staff/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "staff"] }); },
  });
}

export interface StaffSessionRow {
  id: string;
  device_label: string | null;
  login_at: string;
  logout_at: string | null;
  duration_minutes: number | null;
  status: "online" | "completed";
}

export function useStaffSessions(staffId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "staff", staffId, "sessions"],
    queryFn: () => api().get(`admin/staff/${staffId}/sessions`).json<ApiOk<StaffSessionRow[]>>(),
    enabled: !!staffId,
  });
}

export function useResetStaffPin() {
  return useMutation({
    mutationFn: ({ id, newPin }: { id: string; newPin: string }) =>
      api().post(`admin/staff/${id}/reset-pin`, { json: { newPin } }).json(),
  });
}

/* ── Customers ── */
export interface AdminCustomerListItem {
  id: string;
  name: string | null;
  phone: string;
  email: string | null;
  is_active: boolean;
  registered_via: string;
  created_at: string;
  order_count: number;
}

export interface AdminCustomerAddress {
  id: string;
  label: string;
  address: string;
  contact?: string | null;
  is_default: boolean;
}

export interface AdminCustomerOrderRow {
  id: string;
  order_code: string;
  status: string;
  total: string;
  created_at: string;
  type: string;
}

export interface AdminCustomerDetail {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
  password_set_at: string | null;
  registered_via: string;
  phone_verified_at: string | null;
  is_active: boolean;
  notifications_enabled: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  addresses: AdminCustomerAddress[];
  orders: AdminCustomerOrderRow[];
}

export function useAdminCustomersList(params: { q?: string; isActive?: boolean; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ["admin", "customers", "list", params],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (params.q) sp.set("q", params.q);
      if (params.isActive !== undefined) sp.set("isActive", String(params.isActive));
      sp.set("page", String(params.page ?? 1));
      sp.set("limit", String(params.limit ?? 20));
      return api().get(`admin/customers?${sp}`).json<ApiOk<{ items: AdminCustomerListItem[]; total: number; counts: { all: number; active: number; inactive: number } }>>();
    },
  });
}

export function useAdminCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "customers", id],
    queryFn: () => api().get(`admin/customers/${id}`).json<ApiOk<AdminCustomerDetail>>(),
    enabled: !!id,
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; email?: string; is_active?: boolean; notifications_enabled?: boolean }) =>
      api().patch(`admin/customers/${id}`, { json: body }).json<ApiOk<AdminCustomerDetail>>(),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin", "customers"] });
      void qc.invalidateQueries({ queryKey: ["admin", "customers", vars.id] });
    },
  });
}

export function useResetCustomerPassword() {
  return useMutation({
    mutationFn: (id: string) => api().post(`admin/customers/${id}/reset-password`).json<ApiOk<{ ok: boolean; tempPassword: string }>>(),
  });
}

/* ── Reports ── */
export function useRevenueReport(params: { from: string; to: string; granularity?: string }) {
  return useQuery({
    queryKey: ["admin", "reports", "revenue", params],
    queryFn: () => {
      const sp = new URLSearchParams(params as Record<string, string>);
      return api().get(`admin/reports/revenue?${sp}`).json<ApiOk<{ label: string; revenue: string; orders: number; refunds: string; refundCount: number; cancelledRefunds?: string; cancelledRefundCount?: number; netRevenue: string }[]>>();
    },
    enabled: !!(params.from && params.to),
  });
}

export function useStallBreakdown(params: { from: string; to: string }) {
  return useQuery({
    queryKey: ["admin", "reports", "stalls", params],
    queryFn: () => {
      const sp = new URLSearchParams(params);
      return api().get(`admin/reports/stalls?${sp}`).json<ApiOk<{ stallName: string; revenue: string; orders: number; avgOrderValue: string }[]>>();
    },
    enabled: !!(params.from && params.to),
  });
}

export function usePaymentMethodsReport(params: { from: string; to: string }) {
  return useQuery({
    queryKey: ["admin", "reports", "payment-methods", params],
    queryFn: () => {
      const sp = new URLSearchParams(params);
      return api().get(`admin/reports/payment-methods?${sp}`).json<ApiOk<{ method: string; amount: string; pct: number }[]>>();
    },
    enabled: !!(params.from && params.to),
  });
}

export interface PricingBreakdownRow { stallId: string; name: string; orderCount: number; qty: number; basePriceTotal: string; markupTotal: string; bundleDiscountTotal?: string; stallFeeTotal?: string; stallAddonFeeTotal?: string; stallBundleFeeTotal?: string; stallDeductionTotal?: string; platformIncomeTotal?: string; sstTotal?: string; revenue: string }
export interface PricingBreakdownResult {
  month: string;
  stallBreakdown: PricingBreakdownRow[];
  totals: { orderCount: number; qty: number; basePriceTotal: string; markupTotal: string; bundleDiscountTotal?: string; stallFeeTotal?: string; stallAddonFeeTotal?: string; stallBundleFeeTotal?: string; stallDeductionTotal?: string; platformIncomeTotal?: string; sstTotal?: string; revenue: string };
}
export function usePricingBreakdown(params: { month: string; stallId?: string; category?: "walkin" | "customer" }) {
  return useQuery({
    queryKey: ["admin", "reports", "pricing-breakdown", params],
    queryFn: () => {
      const sp = new URLSearchParams({ month: params.month });
      if (params.stallId) sp.set("stallId", params.stallId);
      if (params.category) sp.set("category", params.category);
      return api().get(`admin/reports/pricing-breakdown?${sp}`).json<ApiOk<PricingBreakdownResult>>();
    },
    enabled: !!params.month,
  });
}

export interface TopItemByStallRow { rank: number; name: string; units: number; revenue: string; pct: number; commission?: string; netRevenue?: string }

export function useTopItemsByStall(params: { from?: string; to?: string; limitPerStall?: number; category?: "walkin" | "customer" } = {}) {
  return useQuery({
    queryKey: ["admin", "reports", "top-items-by-stall", params],
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null) sp.set(k, String(v));
      }
      const query = sp.toString();
      return api().get(`admin/reports/top-items-by-stall${query ? `?${query}` : ""}`).json<ApiOk<Record<string, TopItemByStallRow[]>>>();
    },
  });
}

export interface HighRiskOrderRow { id: string; customer: string; amount: string; type: string; reason: string | null; status: "cancelled" | "refunded" }
export interface HighRiskStats { cancelRate: number; refundRate: number; highRiskCount: number; cancelledCount: number; refundedCount: number; refundedAmount: string }

export function useHighRiskOrders(params: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ["admin", "reports", "high-risk", params],
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null) sp.set(k, String(v));
      }
      const query = sp.toString();
      return api().get(`admin/reports/high-risk${query ? `?${query}` : ""}`).json<ApiOk<{ highRisk: HighRiskOrderRow[]; stats: HighRiskStats }>>();
    },
  });
}

export interface StaffSessionReportRow {
  id: string;
  staff_id: string;
  name: string;
  device_label: string | null;
  login_at: string;
  logout_at: string | null;
  duration_minutes: number | null;
  status: "online" | "completed";
}

export function useStaffSessionsReport(params: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: ["admin", "reports", "staff-sessions", params],
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v != null) sp.set(k, String(v));
      }
      const query = sp.toString();
      return api().get(`admin/reports/staff-sessions${query ? `?${query}` : ""}`).json<ApiOk<{ rows: StaffSessionReportRow[]; total: number; onlineStaff: number }>>();
    },
  });
}

/* ── Activity Log ── */
/** Every row matching the filters (the table only holds one page), for CSV export. */
export async function fetchAllActivity(params: Record<string, string | undefined>, cap = 20000): Promise<ActivityLog[]> {
  const out: ActivityLog[] = [];
  for (let page = 1; out.length < cap; page++) {
    const sp = new URLSearchParams({ page: String(page), limit: "200" });
    for (const [k, v] of Object.entries(params)) { if (v) sp.set(k, v); }
    const res = await api().get(`admin/activity?${sp}`).json<PagedOk<ActivityLog>>();
    out.push(...(res.data.items ?? []));
    if (out.length >= res.data.total || !(res.data.items ?? []).length) break;
  }
  return out;
}

export function useActivityLog(params: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) { if (v != null) sp.set(k, String(v)); }
      const res = await api().get(`admin/activity?${sp}`).json<PagedOk<ActivityLog>>();
      return {
        data: res.data.items ?? [],
        meta: { total: res.data.total, page: res.data.page, limit: Number(params.limit ?? 20) },
      };
    },
  });
}

/* ── Feature Flags ── */
export function useFeatureFlags() {
  return useQuery({
    queryKey: ["admin", "flags"],
    queryFn: () => api().get("admin/feature-flags").json<ApiOk<{ id: string; key: string; enabled: boolean; description: string | null }[]>>(),
  });
}

export function useToggleFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      api().patch(`admin/feature-flags/${key}`, { json: { enabled } }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "flags"] }); },
  });
}

/* ── Legal content (Terms / Privacy per app) ── */
export function useLegalDocuments() {
  return useQuery({
    queryKey: ["admin", "legal-documents"],
    queryFn: () => api().get("admin/legal-documents").json<ApiOk<LegalDocument[]>>(),
  });
}

export function useSaveLegalDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appScope, docType, language, title, content }: { appScope: LegalAppScope; docType: LegalDocType; language: LegalLanguage; title: string; content: string }) =>
      api().put(`admin/legal-documents/${appScope}/${docType}/${language}`, { json: { title, content } }).json<ApiOk<LegalDocument>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "legal-documents"] }); },
  });
}

/** Public, unauthenticated fetch — used pre-login on the admin-app's own Login screen. */
export function usePublicLegalDocument(docType: LegalDocType, enabled: boolean) {
  const { i18n } = useTranslation();
  const lang = legalLang(i18n.language);   // the text is shown in the app\'s current language
  return useQuery({
    queryKey: ["legal", "admin", docType, lang],
    queryFn: () => api().get(`public/legal/admin/${docType}?lang=${lang}`).json<ApiOk<{ title: string; content: string; updated_at: string | null }>>(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/* ── Hours & Holidays ── */
export function useKopitiamHours() {
  return useQuery({
    queryKey: ["admin", "hours"],
    queryFn: async () => {
      const res = await api().get("admin/hours").json<ApiOk<{ day_of_week: number; opens_at: string; closes_at: string }[]>>();
      return {
        ...res,
        data: res.data.map((r) => {
          const isClosed = r.opens_at === "00:00" && r.closes_at === "00:00";
          return {
            dayOfWeek: r.day_of_week,
            openTime: isClosed ? "08:00" : r.opens_at,
            closeTime: isClosed ? "22:00" : r.closes_at,
            isClosed,
          };
        }),
      };
    },
  });
}

export function useUpdateHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dayOfWeek: number; openTime: string; closeTime: string; isClosed: boolean }[]) =>
      api().patch("admin/hours", {
        json: {
          entries: body.map((h) => ({
            day_of_week: h.dayOfWeek,
            opens_at: h.isClosed ? "00:00" : h.openTime,
            closes_at: h.isClosed ? "00:00" : h.closeTime,
          })),
        },
      }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "hours"] }); },
  });
}

export function useHolidays() {
  return useQuery({
    queryKey: ["admin", "holidays"],
    queryFn: () => api().get("admin/holidays").json<ApiOk<{ id: string; date: string; note: string | null }[]>>(),
  });
}

export function useAddHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; note?: string }) => api().post("admin/holidays", { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "holidays"] }); },
  });
}

export function useUpdateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; date?: string; note?: string }) =>
      api().patch(`admin/holidays/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "holidays"] }); },
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/holidays/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "holidays"] }); },
  });
}

/* ── Stall Applications ── */
export function useStallApplications(params: { status?: string; page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: ["admin", "stall-applications", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (params.status) sp.set("status", params.status);
      if (params.page) sp.set("page", String(params.page));
      if (params.limit) sp.set("limit", String(params.limit));
      const res = await api().get(`admin/stall-applications?${sp}`).json<PagedOk<StallApplication>>();
      return {
        data: res.data.items ?? [],
        meta: { total: res.data.total, page: res.data.page, limit: params.limit ?? 20 },
      };
    },
  });
}

export function useStallApplication(id: string | null) {
  return useQuery({
    queryKey: ["admin", "stall-applications", id],
    queryFn: () => api().get(`admin/stall-applications/${id}`).json<ApiOk<StallApplication>>(),
    enabled: !!id,
  });
}

export function useReviewStallApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, review_note, sort_order }: { id: string; decision: "approve" | "reject"; review_note?: string; sort_order?: number }) =>
      api().patch(`admin/stall-applications/${id}`, { json: { decision, review_note, sort_order } }).json<ApiOk<StallApplication>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "stall-applications"] }); void qc.invalidateQueries({ queryKey: ["admin", "stalls"] }); },
  });
}

/* ── Settings ── */
/** Google fallback for "find coordinates"; `configured: false` = the server has no GOOGLE_MAPS_API_KEY, so the caller just keeps the OSM result. */
export function geocodeViaGoogle(address: string) {
  return api().post("admin/geocode", { json: { address } }).json<ApiOk<{ configured: boolean; result: { lat: number; lng: number; displayName: string; approximate: boolean } | null }>>();
}

export function useGoogleMapsKey() {
  return useQuery({ queryKey: ["admin", "google-maps-key"], queryFn: () => api().get("admin/integrations/google-maps").json<ApiOk<{ set: boolean; last4: string | null }>>() });
}
export function useSetGoogleMapsKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string | null) => api().put("admin/integrations/google-maps", { json: { key } }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "google-maps-key"] }); },
  });
}

export function useAdminSettings(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => api().get("admin/settings").json<ApiOk<Record<string, unknown>>>(),
    enabled: opts.enabled ?? true,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api().patch("admin/settings", { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "settings"] }); },
  });
}

// Store logo — allowed for both admin and superadmin, unlike the rest of
// tenant Settings (see manage:branding on the backend).
export function useUpdateLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (logo_url: string) => api().patch("admin/settings/logo", { json: { logo_url } }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "settings"] }); },
  });
}

/* ── Payment Method Configs ── */
export function usePaymentMethodConfigs() {
  return useQuery({
    queryKey: ["admin", "payment-methods"],
    queryFn: () => api().get("admin/payment-methods").json<ApiOk<PaymentMethodConfig[]>>(),
  });
}

export function useCreatePaymentMethodConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api().post("admin/payment-methods", { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "payment-methods"] }); },
  });
}

export function useUpdatePaymentMethodConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api().patch(`admin/payment-methods/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "payment-methods"] }); },
  });
}

export function useDeletePaymentMethodConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/payment-methods/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "payment-methods"] }); },
  });
}

export function useTestPaymentMethodConfig() {
  return useMutation({
    mutationFn: (id: string) => api().post(`admin/payment-methods/${id}/test`).json<ApiOk<{ success: boolean; message: string }>>(),
  });
}

/* ── Delivery Providers ── */
export function useDeliveryProviders() {
  return useQuery({
    queryKey: ["admin", "delivery-providers"],
    queryFn: () => api().get("admin/delivery-providers").json<ApiOk<DeliveryProvider[]>>(),
  });
}

export function useCreateDeliveryProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api().post("admin/delivery-providers", { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "delivery-providers"] }); },
  });
}

export function useUpdateDeliveryProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api().patch(`admin/delivery-providers/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "delivery-providers"] }); },
  });
}

export function useDeleteDeliveryProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/delivery-providers/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "delivery-providers"] }); },
  });
}

export function useTestDeliveryProvider() {
  return useMutation({
    mutationFn: (id: string) => api().post(`admin/delivery-providers/${id}/test`).json<ApiOk<{ success: boolean; message: string; latency_ms?: number }>>(),
  });
}

/* ── SMS Providers ── */
export function useSmsProviders() {
  return useQuery({
    queryKey: ["admin", "sms-providers"],
    queryFn: () => api().get("admin/sms-providers").json<ApiOk<SmsProvider[]>>(),
  });
}

export function useCreateSmsProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api().post("admin/sms-providers", { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "sms-providers"] }); },
  });
}

export function useUpdateSmsProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api().patch(`admin/sms-providers/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "sms-providers"] }); },
  });
}

export function useDeleteSmsProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/sms-providers/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "sms-providers"] }); },
  });
}

export function useTestSmsProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, phone }: { id: string; phone: string }) =>
      api().post(`admin/sms-providers/${id}/test`, { json: { phone } }).json<ApiOk<{ success: boolean; message: string; latency_ms?: number }>>(),
    // The backend now updates is_healthy from a manual test result too (not
    // just a live OTP send) — refetch so the Healthy/Unhealthy badge in the
    // list reflects that immediately instead of only after the next reload.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "sms-providers"] }),
  });
}

/** sms123-only: submits message content for their required whitelist/template approval (see backend route doc comment). */
export function useSubmitSmsTemplate() {
  return useMutation({
    mutationFn: ({ id, templateTitle, messageContent }: { id: string; templateTitle: string; messageContent: string }) =>
      api()
        .post(`admin/sms-providers/${id}/submit-template`, { json: { templateTitle, messageContent } })
        .json<ApiOk<{ success: boolean; message: string; raw?: unknown }>>(),
  });
}

/* ── Notifications ── */
export interface AdminNotification {
  id: string;
  type: string;
  order_id: string;
  order_code: string;
  title: string;
  body: string | null;
  is_read: boolean;
  resolved_at: string | null;
  created_at: string;
}

export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    // The backend wraps the list payload as reply.ok({ data, unreadCount }),
    // so unlike other ApiOk<T[]> endpoints here, the array is nested one level
    // deeper at .data.data, with unreadCount alongside it at .data.unreadCount.
    queryFn: () =>
      api().get("staff/notifications", { searchParams: { limit: 20 } })
        .json<ApiOk<{ data: AdminNotification[]; unreadCount: number }>>(),
    // Socket "rider:status_changed" invalidates this on a failed-delivery event;
    // this interval is just the safety net for missed/late socket events.
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().patch(`staff/notifications/${id}/read`).json<ApiOk<AdminNotification>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["notifications"] }); },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api().post("staff/notifications/read-all").json<ApiOk<{ updated: number }>>(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["notifications"] }); },
  });
}

export interface AdminRider {
  id: string;
  name: string;
  platform: string;
  phone: string;
  lalamove_order_id: string | null;
  lalamove_status?: string | null;
  share_link: string | null;
  assigned_at: string;
}

export function useLalamoveResubmit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      api().post(`admin/orders/${orderId}/lalamove/resubmit`).json<ApiOk<AdminRider>>(),
    onSuccess: (_data, orderId) => {
      void qc.invalidateQueries({ queryKey: ["admin", "orders", orderId] });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/* ── Promo Banners ── */
export function useAdminBanners() {
  return useQuery({
    queryKey: ["admin", "banners"],
    queryFn: () => api().get("admin/banners").json<ApiOk<PromoBanner[]>>(),
  });
}

export function useCreateBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api().post("admin/banners", { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "banners"] }); },
  });
}

export function useUpdateBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api().patch(`admin/banners/${id}`, { json: body }).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "banners"] }); },
  });
}

export function useDeleteBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`admin/banners/${id}`).json(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["admin", "banners"] }); },
  });
}

/* ── Stall settlement (snapshot-based gross / net) + ledger CSV exports ── */
export interface SettlementAmounts {
  gross: string; stallFee: string; addonFee: string; bundleFee: string; bundleShare: string; deductions: string; cutlery: string; net: string;
}
export interface SettlementStallRow extends SettlementAmounts { stallId: string; stallName: string; orderCount: number }
export interface SettlementOrderRow extends SettlementAmounts { orderId: string; orderCode: string; createdAt: string; status: string; stallId: string; stallName: string }
export interface SettlementReport {
  range: { from: string; to: string; stallId: string | null };
  stalls: SettlementStallRow[];
  totals: SettlementAmounts & { orderCount: number };
  orders: SettlementOrderRow[];
}
export function useSettlementReport(params: { from: string; to: string; stallId?: string }) {
  return useQuery({
    queryKey: ["admin", "reports", "settlement", params],
    queryFn: () => {
      const sp = new URLSearchParams({ from: params.from, to: params.to });
      if (params.stallId) sp.set("stall_id", params.stallId);
      return api().get(`admin/reports/settlement?${sp}`).json<ApiOk<SettlementReport>>();
    },
    enabled: !!params.from && !!params.to && params.from <= params.to,
  });
}
/** Downloads a CSV from an authenticated admin export endpoint. */
export async function downloadAdminCsv(kind: "settlement" | "order-lines" | "order-bundles", params: { from: string; to: string; stallId?: string }) {
  const sp = new URLSearchParams({ from: params.from, to: params.to });
  if (params.stallId) sp.set("stall_id", params.stallId);
  const blob = await api().get(`admin/exports/${kind}.csv?${sp}`).blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${kind}_${params.from}_${params.to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
